import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  FileClock,
  FilePlus,
  ListFilter,
  Plus,
  Share2,
  Trash2,
  Users,
} from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem, IconButton } from "@doculegis/shared";
import { GlassCard } from "@/components/common/GlassCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ShareReviewDialog } from "@/components/collaboration";
import { NewVersionSheet } from "@/components/review/NewVersionSheet";
import { ReviewProgressLayout } from "@/components/review/ReviewProgressLayout";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { useNewVersion } from "@/hooks/useNewVersion";
import { applySseProgressEvent } from "@/lib/contract-review-sse";
import {
  mapReviewUserError,
  REVIEW_USER_ERROR,
} from "@/lib/review-user-errors";
import type { ProgressState, ReviewFormData, SSEEventData } from "@/types";
import type { SelectedDriveFile } from "@/components/wizard/GoogleDrivePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useReviews } from "@/hooks/useSupabase";
import { usePermission } from "@/contexts/AuthContext";
import {
  formatReviewVersionLabel,
  getReviewDisplayFileName,
} from "@/lib/review-display";
import { getOverallRiskClass } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { pageActionButtonClassName, pageActionDesktopOnlyClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { MobileFab } from "@/components/common/MobileFab";
import type { ReviewWithOwner } from "@/types";

const PAGE_SIZE = 12;

type RiskBucket = "low" | "medium" | "high";

function riskBucketOf(score: number | null): RiskBucket | null {
  if (score === null || score === undefined) return null;
  if (score <= 3) return "low";
  if (score <= 6) return "medium";
  return "high";
}

const RISK_BUCKET_LABELS: Record<RiskBucket, string> = {
  low: "Low (1–3)",
  medium: "Medium (4–6)",
  high: "High (7–10)",
};

interface ReviewFilters {
  search: string;
  contractType: string;
  jurisdiction: string;
  riskBucket: string;
  fromDate: string;
  toDate: string;
}

const EMPTY_FILTERS: ReviewFilters = {
  search: "",
  contractType: "",
  jurisdiction: "",
  riskBucket: "",
  fromDate: "",
  toDate: "",
};

function hasActiveFilters(filters: ReviewFilters): boolean {
  return Boolean(
    filters.search.trim() ||
      filters.contractType ||
      filters.jurisdiction ||
      filters.riskBucket ||
      filters.fromDate ||
      filters.toDate,
  );
}

function FilterBar({
  filters,
  onChange,
  contractTypeOptions,
  jurisdictionOptions,
  mobileSheetOpen,
  onMobileSheetOpenChange,
}: {
  filters: ReviewFilters;
  onChange: (next: ReviewFilters) => void;
  contractTypeOptions: string[];
  jurisdictionOptions: string[];
  mobileSheetOpen: boolean;
  onMobileSheetOpenChange: (open: boolean) => void;
}) {
  const set = (patch: Partial<ReviewFilters>) => onChange({ ...filters, ...patch });

  const dateInputClassName =
    "block box-border h-10 w-full min-w-0 max-w-full border-[rgba(15,23,42,0.09)] bg-white text-[#101828] shadow-[var(--shadow-card)] [color-scheme:light] dark:border-indigo-300/20 dark:bg-slate-950/45 dark:text-slate-100 dark:shadow-none dark:[color-scheme:dark]";
  const searchInputClassName =
    "border-[var(--lm-border-subtle)] bg-white text-sm text-[#101828] shadow-[var(--shadow-card)] placeholder:text-[var(--lm-muted)] dark:border-indigo-300/20 dark:bg-slate-950/45 dark:text-slate-100 dark:shadow-none dark:placeholder:text-slate-500";
  const fieldLabelClassName =
    "mb-1 block text-[11px] font-medium text-[var(--lm-muted)] dark:text-slate-400";

  const renderSearchField = () => (
    <div className="min-w-0">
      <label className={fieldLabelClassName}>File name</label>
      <Input
        placeholder="File name…"
        value={filters.search}
        onChange={(e) => set({ search: e.target.value })}
        className={searchInputClassName}
      />
    </div>
  );

  const renderContractTypeField = () => (
    <div className="min-w-0">
      <label className={fieldLabelClassName}>Contract type</label>
      <Select
        value={filters.contractType || "all"}
        onValueChange={(v) => set({ contractType: v === "all" ? "" : v })}
      >
        <SelectTrigger className="w-full [&>span]:line-clamp-1 [&>span]:truncate">
          <SelectValue placeholder="All types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All types</SelectItem>
          {contractTypeOptions.map((t) => (
            <SelectItem key={t} value={t}>
              {t}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderJurisdictionField = () => (
    <div className="min-w-0">
      <label className={fieldLabelClassName}>Jurisdiction</label>
      <Select
        value={filters.jurisdiction || "all"}
        onValueChange={(v) => set({ jurisdiction: v === "all" ? "" : v })}
      >
        <SelectTrigger className="w-full [&>span]:line-clamp-1 [&>span]:truncate">
          <SelectValue placeholder="All jurisdictions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All jurisdictions</SelectItem>
          {jurisdictionOptions.map((j) => (
            <SelectItem key={j} value={j}>
              {j}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderRiskField = () => (
    <div className="min-w-0">
      <label className={fieldLabelClassName}>Risk level</label>
      <Select
        value={filters.riskBucket || "all"}
        onValueChange={(v) => set({ riskBucket: v === "all" ? "" : v })}
      >
        <SelectTrigger className="w-full [&>span]:line-clamp-1 [&>span]:truncate">
          <SelectValue placeholder="All risk levels" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All risk levels</SelectItem>
          {(Object.keys(RISK_BUCKET_LABELS) as RiskBucket[]).map((key) => (
            <SelectItem key={key} value={key}>
              {RISK_BUCKET_LABELS[key]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderFromDateField = () => (
    <div className="min-w-0">
      <label className={fieldLabelClassName}>From date</label>
      <Input
        type="date"
        value={filters.fromDate}
        onChange={(e) => set({ fromDate: e.target.value })}
        className={dateInputClassName}
      />
    </div>
  );

  const renderToDateField = () => (
    <div className="min-w-0">
      <label className={fieldLabelClassName}>To date</label>
      <Input
        type="date"
        value={filters.toDate}
        onChange={(e) => set({ toDate: e.target.value })}
        className={dateInputClassName}
      />
    </div>
  );

  const renderClearButton = () =>
    hasActiveFilters(filters) ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mb-0.5 shrink-0 whitespace-nowrap"
        onClick={() => onChange(EMPTY_FILTERS)}
      >
        Clear
      </Button>
    ) : null;

  return (
    <>
      <Sheet open={mobileSheetOpen} onOpenChange={onMobileSheetOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-3">
            {renderSearchField()}
            {renderContractTypeField()}
            {renderJurisdictionField()}
            {renderRiskField()}
            {renderFromDateField()}
            {renderToDateField()}
            {renderClearButton()}
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Tablet / desktop: flat filter bar (mockup) */}
      <div
        className={cn(
          "mb-4 hidden w-full flex-wrap items-end gap-2.5 sm:flex",
        )}
      >
        {renderSearchField()}
        {renderContractTypeField()}
        {renderJurisdictionField()}
        {renderRiskField()}
        {renderFromDateField()}
        {renderToDateField()}
        {renderClearButton()}
      </div>
    </>
  );
}

function ReviewPagination({
  page,
  pageCount,
  totalItems,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}) {
  if (totalItems <= PAGE_SIZE) return null;

  return (
    <div className="mt-5 flex items-center justify-center gap-3 text-[13px] text-[var(--lm-muted)] dark:mt-4 dark:border-t dark:border-indigo-300/15 dark:pt-4 dark:text-slate-400">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="px-2.5"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-[13px] text-[var(--lm-muted)] dark:text-slate-300">
        Page {page} of {pageCount} · {totalItems} review{totalItems === 1 ? "" : "s"}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="px-2.5"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ReviewCard({
  review,
  onShare,
  onDelete,
  onNewVersion,
  canDelete,
  canShare,
  canNewVersion,
}: {
  review: ReviewWithOwner;
  onShare?: (reviewId: string) => void;
  onDelete?: (review: ReviewWithOwner) => void;
  onNewVersion?: (review: ReviewWithOwner) => void;
  canDelete: boolean;
  canShare: boolean;
  canNewVersion: boolean;
}) {
  const fullFileName = review.file_name?.trim() || null;
  const displayFileName = getReviewDisplayFileName(review.file_name);

  const showDelete = canDelete && onDelete && review.is_owner;
  const showShare = Boolean(review.is_owner && onShare && canShare);
  const showNewVersion = Boolean(review.is_owner && onNewVersion && canNewVersion);
  const showOwnerActions = review.is_owner && (showShare || showNewVersion || showDelete);
  const shareReviewId = review.root_review_id ?? review.id;

  return (
    <div className="group relative min-w-0">
      {showOwnerActions ? (
        <div className="absolute right-3 top-3 z-10">
          <ActionOverflowMenu
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {showShare ? (
              <ActionOverflowMenuItem
                icon={Share2}
                label="Share review"
                onClick={() => onShare!(shareReviewId)}
              />
            ) : null}
            {showNewVersion ? (
              <ActionOverflowMenuItem
                icon={FilePlus}
                label="New version"
                onClick={() => onNewVersion!(review)}
              />
            ) : null}
            {showDelete ? (
              <ActionOverflowMenuItem
                icon={Trash2}
                label="Delete review"
                variant="destructive"
                onClick={() => onDelete!(review)}
              />
            ) : null}
          </ActionOverflowMenu>
        </div>
      ) : !review.is_owner ? (
        <span className="absolute right-3 top-3 z-10 inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--tint-purple-bg)] px-2 py-0.5 text-xs text-[var(--tint-purple-fg)] dark:bg-purple-500/20 dark:text-purple-300">
          <Users className="h-3 w-3" />
          Shared
        </span>
      ) : null}

      <Link
        to={`/contract-review/${review.id}`}
        className="block min-w-0 rounded-xl border border-[var(--lm-border-card)] bg-[var(--surface-sunken)] p-4 transition-all hover:border-[rgba(147,51,245,0.4)] dark:rounded-2xl dark:border-indigo-300/20 dark:bg-slate-900/45 dark:hover:border-violet-300/30 dark:hover:bg-slate-900/60"
      >
        <p
          className="mb-2 truncate pr-[60px] text-sm font-semibold leading-snug text-[var(--lm-foreground-strong)] dark:text-slate-100"
          title={fullFileName ?? undefined}
        >
          {displayFileName}
        </p>
        <p className="truncate text-xs leading-relaxed text-[var(--lm-muted)] dark:text-slate-300/85">
          {review.contract_type || "Unknown type"}
        </p>
        {review.jurisdiction ? (
          <p className="mt-0.5 truncate text-xs text-[var(--lm-muted)] dark:text-slate-400">
            {review.jurisdiction}
          </p>
        ) : null}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
            {new Date(review.created_at).toLocaleString()}
          </p>
          {(review.version_count ?? 1) > 1 ? (
            <span className="inline-flex rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300">
              {review.version ??
                formatReviewVersionLabel(review.version_number, review.created_at)}
            </span>
          ) : null}
        </div>
        <span
          className={cn(
            "mt-2.5 inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
            getOverallRiskClass(review.overall_risk_score ?? 0),
          )}
        >
          Risk {review.overall_risk_score ?? "-"} / 10
        </span>
      </Link>

      {!review.is_owner && review.owner_name ? (
        <p className="mt-2 truncate px-1 text-xs text-[var(--lm-muted)] dark:text-slate-400">
          Shared by <span className="text-[#7c3aed] dark:text-purple-300">{review.owner_name}</span>
        </p>
      ) : null}
    </div>
  );
}

function applyFilters(list: ReviewWithOwner[], filters: ReviewFilters): ReviewWithOwner[] {
  let result = list;
  const q = filters.search.trim().toLowerCase();
  if (q) {
    result = result.filter((r) => (r.file_name ?? "").toLowerCase().includes(q));
  }
  if (filters.contractType) {
    result = result.filter((r) => r.contract_type === filters.contractType);
  }
  if (filters.jurisdiction) {
    result = result.filter((r) => r.jurisdiction === filters.jurisdiction);
  }
  if (filters.riskBucket) {
    const bucket = filters.riskBucket as RiskBucket;
    result = result.filter((r) => riskBucketOf(r.overall_risk_score) === bucket);
  }
  if (filters.fromDate) {
    const from = new Date(filters.fromDate);
    from.setHours(0, 0, 0, 0);
    result = result.filter((r) => new Date(r.created_at) >= from);
  }
  if (filters.toDate) {
    const to = new Date(filters.toDate);
    to.setHours(23, 59, 59, 999);
    result = result.filter((r) => new Date(r.created_at) <= to);
  }
  return result;
}

export function DashboardHome() {
  const navigate = useNavigate();
  const { reviews, isLoading, error, deleteReview, refresh } = useReviews();
  const { createNewVersion } = useNewVersion();
  const canCreate = usePermission("review", "create");
  const canDelete = usePermission("review", "delete");
  const canShare = usePermission("review", "share");
  const [filters, setFilters] = useState<ReviewFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<ReviewWithOwner | null>(null);
  const [shareTargetId, setShareTargetId] = useState<string | null>(null);
  const [newVersionTarget, setNewVersionTarget] = useState<ReviewWithOwner | null>(null);
  const [newVersionProgress, setNewVersionProgress] = useState<ProgressState | null>(null);
  const [newVersionError, setNewVersionError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const visibleReviews = useMemo(
    () => applyFilters(reviews, filters),
    [reviews, filters],
  );

  const pageCount = Math.max(1, Math.ceil(visibleReviews.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return visibleReviews.slice(start, start + PAGE_SIZE);
  }, [visibleReviews, page]);

  const contractTypeOptions = useMemo(() => {
    const set = new Set<string>();
    reviews.forEach((r) => {
      if (r.contract_type) set.add(r.contract_type);
    });
    return Array.from(set).sort();
  }, [reviews]);

  const jurisdictionOptions = useMemo(() => {
    const set = new Set<string>();
    reviews.forEach((r) => {
      if (r.jurisdiction) set.add(r.jurisdiction);
    });
    return Array.from(set).sort();
  }, [reviews]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    if (!deleteSuccessMessage) return;
    const timer = window.setTimeout(() => setDeleteSuccessMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [deleteSuccessMessage]);

  const handleNewVersionSubmit = async (
    file: File | null,
    driveFile: SelectedDriveFile | null,
    formData: ReviewFormData,
  ) => {
    if (!newVersionTarget) return;
    setNewVersionTarget(null);
    setNewVersionError(null);
    setNewVersionProgress({
      message: "Connecting...",
      progress: 0,
      isConnected: false,
      logs: [],
    });

    try {
      const result = await createNewVersion(newVersionTarget.id, file, driveFile, formData, {
        onProgress: (data: SSEEventData) => {
          setNewVersionProgress((prev) => (prev ? applySseProgressEvent(prev, data) : prev));
        },
        onHeartbeat: () => {
          setNewVersionProgress((prev) => (prev ? { ...prev, isConnected: true } : prev));
        },
      });

      if (result?.reviewId) {
        navigate(`/contract-review/${result.reviewId}`);
      } else {
        setNewVersionError(REVIEW_USER_ERROR.createNewVersionFailed);
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : REVIEW_USER_ERROR.generic;
      setNewVersionError(mapReviewUserError(raw));
    } finally {
      setNewVersionProgress(null);
      void refresh();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteReview(deleteTarget.id, deleteTarget.contract_file_path);
      setDeleteSuccessMessage(`"${deleteTarget.file_name || "Review"}" deleted.`);
      setDeleteTarget(null);
    } catch (e: unknown) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsDeleting(false);
    }
  };

  const emptyBecauseFilters = !isLoading && !error && visibleReviews.length === 0 && reviews.length > 0;
  const accessDenied = error?.toLowerCase().includes("insufficient permissions");

  if (newVersionProgress || newVersionError) {
    return (
      <ReviewProgressLayout
        progress={newVersionProgress ?? undefined}
        error={newVersionError}
        onErrorDismiss={() => setNewVersionError(null)}
      />
    );
  }

  return (
    <section className="relative px-4 pt-4 md:px-8 md:pt-8">
      <div className="pointer-events-none absolute inset-0 hidden bg-[radial-gradient(circle_at_18%_20%,rgba(123,97,255,0.18),transparent_36%),radial-gradient(circle_at_84%_12%,rgba(63,146,255,0.15),transparent_34%)] dark:block" />
      <div className="relative">
        <div className="mb-5 flex flex-row items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              Contract Reviews
            </h1>
            <p className="hidden text-sm text-[var(--lm-muted)] sm:block dark:text-slate-300/85">
              Browse your contract analyses and shared reviews
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!isLoading && reviews.length > 0 ? (
              <div className="relative sm:hidden">
                <IconButton
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  title="Filters"
                  aria-label="Filters"
                  className="rounded-lg border-transparent bg-[var(--nav-active-bg)] text-[#7c3aed] hover:border-transparent hover:bg-[rgba(147,51,245,0.15)] hover:text-[#6d28d9] dark:bg-indigo-500/20 dark:text-violet-300 dark:hover:bg-indigo-500/30 dark:hover:text-violet-200"
                >
                  <ListFilter strokeWidth={1.75} />
                </IconButton>
                {hasActiveFilters(filters) ? (
                  <span
                    className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#9333F5] dark:border-slate-900"
                    aria-hidden
                  />
                ) : null}
              </div>
            ) : null}
            {canCreate ? (
              <Button
                variant="accent"
                asChild
                title="New review"
                aria-label="New review"
                className={cn(pageActionButtonClassName, pageActionDesktopOnlyClassName)}
              >
                <Link to="/contract-review/new">
                  <Plus className="h-4 w-4" />
                  <span className={pageActionLabelClassName}>New review</span>
                </Link>
              </Button>
            ) : null}
          </div>
        </div>

        {deleteSuccessMessage ? (
          <p className="mb-3 rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-300">
            {deleteSuccessMessage}
          </p>
        ) : null}

        {!isLoading && reviews.length > 0 ? (
          <FilterBar
            filters={filters}
            onChange={setFilters}
            contractTypeOptions={contractTypeOptions}
            jurisdictionOptions={jurisdictionOptions}
            mobileSheetOpen={mobileFiltersOpen}
            onMobileSheetOpenChange={setMobileFiltersOpen}
          />
        ) : null}

        <GlassCard className="mb-2 border-[var(--lm-border-card)] bg-white px-4 pb-24 pt-4 shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none sm:pb-10 md:px-6 md:pb-12 md:pt-6">
          {isLoading ? <p className="text-sm text-slate-300/85">Loading reviews...</p> : null}
          {accessDenied ? (
            <div className="rounded-xl border border-indigo-300/20 bg-slate-900/45 p-6 text-center">
              <FileClock className="mx-auto mb-3 h-10 w-10 text-violet-200" />
              <h2 className="text-base font-semibold text-slate-100">Access denied</h2>
              <p className="mt-1 text-sm text-slate-300/85">
                You don&apos;t have permission to view contract reviews. Contact your organization owner if
                you need access.
              </p>
            </div>
          ) : null}
          {error && !accessDenied ? <p className="text-sm text-red-300">{error}</p> : null}
          {deleteError ? <p className="mb-3 text-sm text-red-300">{deleteError}</p> : null}
          {newVersionError ? (
            <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
              <ErrorAlert message={newVersionError} className="mt-0" />
              <button
                type="button"
                onClick={() => setNewVersionError(null)}
                className="mt-2 text-xs text-slate-400 underline hover:text-slate-200"
              >
                Dismiss
              </button>
            </div>
          ) : null}

          {!isLoading && !error && visibleReviews.length === 0 ? (
            <div className="rounded-xl border border-indigo-300/20 bg-slate-900/45 p-6 text-center">
              <FileClock className="mx-auto mb-3 h-10 w-10 text-violet-200" />
              <h2 className="text-base font-semibold text-slate-100">
                {emptyBecauseFilters ? "No reviews match your filters" : "No reviews yet"}
              </h2>
              <p className="mt-1 text-sm text-slate-300/85">
                {emptyBecauseFilters
                  ? "Try adjusting or clearing your filters."
                  : "Run your first contract review to see it here."}
              </p>
              {emptyBecauseFilters ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-3"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                >
                  Clear filters
                </Button>
              ) : null}
            </div>
          ) : null}

          {pageItems.length > 0 ? (
            <>
              <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
                {pageItems.map((review) => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onShare={setShareTargetId}
                    onNewVersion={setNewVersionTarget}
                    onDelete={setDeleteTarget}
                    canDelete={canDelete}
                    canShare={canShare}
                    canNewVersion={canCreate}
                  />
                ))}
              </div>
              <ReviewPagination
                page={page}
                pageCount={pageCount}
                totalItems={visibleReviews.length}
                onPageChange={setPage}
              />
            </>
          ) : null}
          {!isLoading && pageItems.length > 0 ? <div className="h-4 shrink-0" aria-hidden /> : null}
        </GlassCard>
      </div>

      {canCreate ? (
        <MobileFab to="/contract-review/new" label="New review" />
      ) : null}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => {
          if (!isDeleting) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete review?"
        description={
          deleteTarget
            ? `Permanently delete "${deleteTarget.file_name || "this review"}" and all of its versions? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={isDeleting}
      />

      {shareTargetId ? (
        <ShareReviewDialog
          reviewId={shareTargetId}
          isOpen={true}
          onClose={() => setShareTargetId(null)}
        />
      ) : null}

      {newVersionTarget ? (
        <NewVersionSheet
          sourceReview={newVersionTarget}
          isOpen={true}
          isSubmitting={newVersionProgress !== null}
          onClose={() => setNewVersionTarget(null)}
          onSubmit={handleNewVersionSubmit}
        />
      ) : null}

    </section>
  );
}
